import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppMaterialModule } from 'src/app/app-material.module';
import {
  MatCheckbox,
  MatCheckboxDefaultOptions,
  MAT_CHECKBOX_DEFAULT_OPTIONS,
} from '@angular/material/checkbox';

import { TruncatePipe } from '../truncate.pipe';
import {
  FIELDTYPE,
  IChipEntry,
  IDropDownListEntry,
} from './mdm-input-settings';

@Component({
  selector: 'app-mdm-input',
  templateUrl: './mdm-input.component.html',
  styleUrls: ['./mdm-input.component.scss'],
  imports: [
    CommonModule,
    AppMaterialModule,
    FormsModule,
    ReactiveFormsModule,
    TruncatePipe,
  ],
  providers: [
    {
      provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
      useValue: { clickAction: 'noop' } as MatCheckboxDefaultOptions,
    },
  ],
})
export class MdmInputComponent implements OnInit {
  @ViewChild('filter', { static: false }) input: ElementRef<HTMLInputElement>;
  @ViewChild('selectAllCheckbox') selectAllCheckbox: MatCheckbox;

  public removable = true;
  public alreadySelected: boolean = false;
  public minimumLengthError: boolean = false;
  public filterText: string = '';
  public sliderValue: number = 0;
  public moreChips: number = -1;

  public filteredDropDownData: IDropDownListEntry[] = [];

  @Input() disabled: boolean = false;
  @Input() scrollChips: boolean = false;
  @Input() dropDownSelect: boolean = false;
  @Input() clearFilterOnDropDownClose: boolean = true;
  @Input() fieldType: FIELDTYPE = FIELDTYPE.STRING;
  @Input() noDataAvailableText: string = 'No data available';
  @Input() noFilteredDataAvailableText: string = 'No filterd data';
  @Input() dropDownOpen: boolean = false;
  @Input() singleSelect: boolean = false;
  @Input() dropDownData: IDropDownListEntry[] = [];
  @Input() searchPlaceholderText: string = 'Search for';
  @Input() placeholder: string = '';
  @Input() allowSearchFilter: boolean = true;
  @Input() allowSelectAll: boolean = false;
  @Input() unSelectAllText: string = 'Deselect All';
  @Input() selectAllText: string = 'Select All';
  @Input() insideLabel: string = '';
  @Input() outsideLabel: string = '';
  @Input() minimumFilterLength: number = 0;
  @Input() selectLimit: number = 0;
  @Input() minimumLength: number = 0;
  @Input() allowSpaces: boolean = true;
  @Input() allowSingleChip: boolean = true;
  @Input() maxChipTextLength: number = 6;
  @Input() maxChipsToDisplay: number = 0;

  @Input() chips: IChipEntry[] = [];
  @Output() chipsChange: EventEmitter<IChipEntry[]> = new EventEmitter<
    IChipEntry[]
  >();

  @Input() text: string = '';
  @Output() textChange: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  selectionChanged: EventEmitter<IChipEntry[]> = new EventEmitter<
    IChipEntry[]
  >();
  @Output()
  keyPress: EventEmitter<any> = new EventEmitter<any>();

  constructor(private eRef: ElementRef) {}

  ngOnInit(): void {
    if (this.minimumFilterLength == 0)
      this.filteredDropDownData = this.dropDownData;
  }

  @HostListener('document:click', ['$event'])
  clickout(event: any) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.dropDownOpen = false;
    }
  }

  removeChip(value: IChipEntry) {
    if (this.dropDownSelect) {
      let f = this.dropDownData.find(d => {
        return d.key == value.key;
      });
      if (f != null) {
        f.isSelected = false;
      }
    }
    let i = this.chips.indexOf(value);
    this.chips.forEach(c => {
      if (c.key == value.key) i = this.chips.indexOf(c);
    });
    this.chips.splice(i, 1);

    if (this.sliderValue > 0) this.sliderValue--;
    this.selectionChanged.emit(this.chips);
  }

  addDropDownChip(chip: IChipEntry): void {
    if (this.singleSelect && this.chips.length > 0) {
      let remove = this.chips[0];
      this.removeChip(remove);
    }
    this.chips.push(chip);

    if (this.selectLimit > 0 && this.chips.length >= this.selectLimit) {
      this.closeDropdown();
    }
    this.selectionChanged.emit(this.chips);
  }

  displayableChips(): IChipEntry[] {
    if (!this.scrollChips) {
      if (this.maxChipsToDisplay == 0) return this.chips;
      else {
        let ret = this.chips.slice(0, this.maxChipsToDisplay);
        this.moreChips = this.chips.length - this.maxChipsToDisplay;
        return ret;
      }
    }
    let start = Math.min(this.sliderValue, this.chips.length);
    let end = Math.min(start + 3, this.chips.length);
    return this.chips.slice(start, end);
  }

  addTextChip(): boolean {
    this.alreadySelected = false;
    this.minimumLengthError = false;

    if (this.text.trim().length == 0) return false;

    if (this.minimumLength === undefined) {
      return false;
    }

    if (this.text.trim().length < this.minimumLength) {
      this.minimumLengthError = true;
      return false;
    }

    if (this.chipExists(this.text)) {
      this.alreadySelected = true;
      return false;
    }

    let chip: IChipEntry = { key: this.text, value: this.text };

    this.chips.push(chip);
    this.selectionChanged.emit(this.chips);
    this.text = '';
    return true;
  }

  onFilterChange() {
    if (this.filterText.trim().length >= this.minimumFilterLength) {
      this.filteredDropDownData = this.dropDownData.filter(d => {
        return (
          d.value.toLowerCase().indexOf(this.filterText.toLowerCase()) > -1
        );
      });
    } else {
      this.filteredDropDownData = [];
    }
  }

  canSelectMore(): boolean {
    return this.selectLimit <= 0 || this.chips.length < this.selectLimit;
  }

  chipExists(key: string): boolean {
    let chip: IChipEntry = { key: '', value: '' };
    this.chips.forEach(c => {
      if (c.key == key) chip = c;
    });
    return chip.key == key;
  }

  onTextChange(): void {
    this.textChange.emit(this.text);
  }

  keyPressed(event: any): boolean {
    this.keyPress.emit(event);
    const key = event.key;

    if (this.disabled) return false;

    if (key == 'Enter' && (this.selectLimit != 1 || this.allowSingleChip)) {
      return this.addTextChip();
    }

    if (key == ' ' && !this.allowSpaces) return false;

    if (this.fieldType == FIELDTYPE.STRING) return true;

    // allow copy and paste
    if (
      (event.ctrlKey || event.metaKey) && // control key pressed
      (event.keyCode == 67 || event.keyCode == 86)
    )
      // c and v respectively
      return true;

    if (this.text.length == 0 && key == '-') return true;

    const allowedNumericKeys = [',', '.'];
    if (this.fieldType == FIELDTYPE.NUMBER && allowedNumericKeys.includes(key))
      return true;

    const allowedNavigationKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
    ];
    const digits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

    return digits.includes(key) || allowedNavigationKeys.includes(key);
  }

  toggleDropdown() {
    if (this.disabled) return;
    this.dropDownOpen = !this.dropDownOpen;

    if (this.clearFilterOnDropDownClose) {
      this.filterText = '';
      this.filteredDropDownData = this.dropDownData;
    }
    if (
      this.dropDownOpen &&
      this.filterText.trim().length < this.minimumFilterLength
    )
      this.filteredDropDownData = [];
    if (this.dropDownOpen && this.input != undefined) {
      setTimeout(() => {
        this.input.nativeElement.focus();
      }, 0);
    }
  }

  closeDropdown() {
    this.dropDownOpen = false;
    // clear search text
    if (this.clearFilterOnDropDownClose) {
      this.filterText = '';
      this.filteredDropDownData = this.dropDownData;
    }
  }

  listItemChange(key: string, e: any) {
    let value = '';
    this.dropDownData.forEach(d => {
      if (d.key == key) {
        d.isSelected = e.target.checked;
        value = d.value;
      }
    });
    let chip: IChipEntry = { key: key, value: value };
    if (e.target.checked) {
      this.addDropDownChip(chip);
    } else {
      this.removeChip(chip);
    }
  }

  toggleSelectAll(): boolean {
    if (!this.selectAllCheckbox.checked) {
      this.filteredDropDownData.forEach(d => {
        if (!d.isSelected) {
          d.isSelected = true;
          let chip: IChipEntry = { key: d.key, value: d.value };
          this.addDropDownChip(chip);
        }
      });
    } else {
      this.dropDownData.forEach(d => {
        d.isSelected = false;
      });
      this.chips.length = 0;
    }
    return true;
  }

  isAllItemsSelected(): boolean {
    return (
      this.dropDownData.filter(ddd => {
        return !ddd.isSelected;
      }).length == 0
    );
  }

  itemsRemaining(): number {
    if (!this.chips) return 0;
    if (this.selectLimit == undefined || this.selectLimit <= 0) return 0;
    return this.selectLimit - this.chips.length;
  }
}
